import React, { useEffect, useMemo, useState } from "react";

import styles from "./streamDetail.module.scss";
import { useSelector } from "react-redux";
import cx from "classnames";
import _ from "lodash";
import { useTransition, animated } from "@react-spring/web";
import InfiniteScroll from "src/components/infiniteScroll";
import { useGroups } from "src/pages/groups";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Asset } from "src/types/assets";
import { Group } from "src/types/groups";
import AssetIcon from "src/components/asset/icon";
import { Col, Row } from "react-bootstrap";
import GroupIcon from "src/components/groupIcon";
import { fetchStreamChildren } from "src/redux/actions/streams";
import { fetchGroupChildren } from "src/redux/actions/groups";
import { FileService } from "src/utils/file.service";
import dateFormat from "dateformat";
import StreamHeader from "../streamHeader";
import userRoleService from "src/utils/userRoles.service";
import { getStreamSortPreference } from "src/utils/stream.helper";
import { NavigateEntity } from "src/components/NavigateEntity";
import Text from "src/components/text";


interface StreamDetailViewersProps {
  entityType: 'stream' | 'group';
  updating?: boolean;
  viewer: string,
  groups: any,
  assets: any,
  editAction: string,
  invalidateSelectedEntities: boolean,
  handleUpdateEntities: (entities: any) => void;
  handleSelectEntity: (entities: any) => void;
}

const StreamDetailViewers: React.FC<StreamDetailViewersProps> = ({ entityType, updating, viewer, groups, assets, editAction, handleUpdateEntities, handleSelectEntity, invalidateSelectedEntities }) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();

  const allEntities = [...groups, ...assets];
  const analytics = useSelector((state: any) => state.asset.analytics);


  const [value, setValue] = useState<{ [key: string]: string }>({});
  const [selectedEntities, setSelectedEntities] = useState<Asset[] | Group[]>([]);
  const [sortPreference, setSortPreference] = useState<{ sortBy: string; orderBy: string }>({ sortBy: "name", orderBy: "ASC" });
  const [hoveredEntity, setHoveredEntity] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);

  const streamsData = useStreams(id || "");
  const groupsData = useGroups(id || "");

  const loading = entityType === 'stream' ? streamsData.loading : groupsData.loading;
  const hasMoreItems = entityType === 'stream' ? streamsData.hasMoreItems : groupsData.hasMoreItems;
  const nextPage = entityType === 'stream' ? streamsData.nextPage : groupsData.nextPage;

  useEffect(() => {
    const sortPreference = getStreamSortPreference();
    if (sortPreference) {
      setSortPreference(sortPreference);
    }
  }, [])

  useEffect(() => {
    setValue({});
  }, [editAction])

  useEffect(() => {
    handleUpdateEntities(selectedEntities);
  }, [selectedEntities])

  useEffect(() => {
    if (invalidateSelectedEntities) {
      setSelectedGroups([]);
      setSelectedAssets([]);
    }
  }, [invalidateSelectedEntities])

  const transitions = useTransition(viewer, {
    from: { opacity: 0, transform: 'translate3d(0, 100px, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
  });


  const handleChange = (e: any, entity: Asset | Group) => {
    const value = e.target.value;

    setValue((prevValue) => ({
      ...prevValue,
      [entity.id]: value
    }));

    if (value.trim() && !_.isEqual(entity.name.trim(), value.trim())) {
      const updatedEntity = {
        id: entity.id,
        name: value,
        entityType: entity.entityType,
        metadata: _.get(entity, "metadata"),
      }

      setSelectedEntities((prev) => {
        const itemExists = prev.some((item) => item.id === entity.id);

        if (itemExists) {
          return prev.map((item) =>
            item.id === entity.id ? updatedEntity : item
          );
        }

        return [...prev, updatedEntity];
      });
    }
  }

  const handleClick = (entity: Asset | Group) => {
    const isEditStream = editAction === "editStream" || editAction === "editGroup";

    if(isEditStream){
      if(entity.entityType === "group"){
        handleGroupClick(entity as Group);
      } else if(entity.entityType === "asset"){
        handleAssetClick(entity as Asset);
      }
    } else {
      const basePath = entity.entityType === "asset" ? "/assets" : "/groups";
      navigate(`${basePath}/${entity.id}?source=${entityType}s/${id}`);
    }
  }

  const timeSpentValue = (timeSpent: number) => {
    let timeSpentValue = `~${FileService.padNumber(timeSpent, 1)}s`;

    if (timeSpent >= 60) {
      timeSpentValue = `~${FileService.padNumber(Math.round(timeSpent / 60), 1)}m`;
    }

    return timeSpentValue;
  };

  const handleGroupClick = (group: Group) => {
    if (isGroupSelected(group)) {
      setSelectedGroups(selectedGroups.filter((selectedGroup) => selectedGroup.id !== group.id))
    } else {
      setSelectedGroups([...selectedGroups, group])
    }

    handleSelectEntity && handleSelectEntity(group);
  }

  const handleAssetClick = (asset: Asset) => {
    if (isAssetSelected(asset)) {
      setSelectedAssets(selectedAssets.filter((selectedAsset) => selectedAsset.id !== asset.id))
    } else {
      setSelectedAssets([...selectedAssets, asset])
    }

    handleSelectEntity && handleSelectEntity(asset);
  }

  const isGroupSelected = (group: Group) => {
    return selectedGroups.some((selectedGroup) => selectedGroup.id === group.id)
  }

  const isAssetSelected = (asset: Asset) => {
    return selectedAssets.some((selectedAsset) => selectedAsset.id === asset.id)
  }

  const renderMetadata = (entity: Asset | Group) => {
    if (entity.entityType === "asset") {
      const metadata = _.get(entity, "metadata");
      const assetType = metadata ? _.upperCase(FileService.getFileType(metadata)) : "";
      const assetSize = metadata ? FileService.formatSize(metadata.content_length) : "";
      const fileType = FileService.getFileType(metadata);
      return (
        <>
          <span className={cx(styles.metadata, {
            [styles.editAction]: editAction === "rename"
          })}>
            {fileType !== "INTERACTIVE" && assetType !== "LINK" && (
              <> 
              <span>{assetSize} </span>
              <span className={styles.separator}></span>
              </>
            )}
            <span> {assetType} </span>
          </span>
        </>
      );
    }
    return (
      <>
        <span className={cx(styles.metadata, {
          [styles.editAction]: editAction === "rename"
        })}>Group</span>
      </>
    );
  }

  const getCreatedBy = useMemo(() => {
    return (entity: Asset | Group) => {
      const firstName = _.get(entity, "createdBy.firstName") || _.get(entity, "author.firstName");
      const lastName = _.get(entity, "createdBy.lastName") || _.get(entity, "author.lastName");
      return `${firstName}${lastName ? ` ${lastName}` : ""}`;
    };
  }, []);
  
  const renderEntityDetails = (entity: Asset | Group) => {
    const isEditStream = editAction === "editStream" || editAction === "editGroup";
    const canSelectEntity = editAction !== "rename";
    const assetAnalytics = _.get(analytics, `${entity.id}`);
    const views = _.get(assetAnalytics, `views`);
    const downloads = _.get(assetAnalytics, `downloads`);
    const shares = _.get(assetAnalytics, `shares`);
    const timeSpent = entity.entityType === "asset" ? timeSpentValue(_.get(assetAnalytics, `avgTimeSpent`)) : null;
    const createdBy = getCreatedBy(entity);
    let modifiedDate = entity.modifiedDate;

    if (entity.entityType === "asset" && _.has(entity, "metadata") && _.get(entity, "metadata.modified_date")) {
      modifiedDate = _.get(entity, "metadata.modified_date");
    }


    return transitions((style, item) =>
      item ? (
        <animated.div key={entity.id} style={style as any}>
          <NavigateEntity
          path={`/${entity.entityType === "asset" ? "assets" : "groups"}/${entity.id}?source=${entityType}s/${id}`}
          >
          <Row
            key={entity.id}
            className={styles[`${entity.entityType}Component`]}
            onClick={() => canSelectEntity && handleClick(entity)}
            onMouseEnter={() => setHoveredEntity(entity.id)}
            onMouseLeave={() => setHoveredEntity("")}
          >
            <Col
              md={viewer === "list" ? 3 : 12}
              className={styles[`${entity.entityType}Details`]}
            >
              {entity.entityType === "asset" && (
                <AssetIcon
                  asset={entity as Asset}
                  view={viewer}
                  isEditStream={isEditStream}
                  hoveredEntity={hoveredEntity}
                  isAssetSelected={isAssetSelected}
                />
              )}
              {entity.entityType === "group" && (
                <GroupIcon
                  group={entity as Group}
                  view={viewer}
                  isEditStream={isEditStream}
                  hoveredEntity={hoveredEntity}
                  isGroupSelected={isGroupSelected}
                />
              )}

              <div className={styles.detailsWrapper}>
                <div
                  className={cx(styles.nameWrapper, {
                    [styles.editName]: editAction === "rename",
                  })}
                >
                  {editAction === "rename" ? (
                    <input
                      disabled={updating}
                      type="text"
                      className={cx(
                        styles.name,
                        styles.rename,
                        updating && styles.updating,
                      )}
                      value={value[entity.id] ?? entity.name}
                      onChange={(e) => handleChange(e, entity)}
                    />
                  ) : (
                    <Text className={styles.name} text={entity?.name || ""} lines={2}  />
                  )}
                </div>

                {viewer !== "card" && renderMetadata(entity)}
              </div>
            </Col>

            {viewer === "list" && (
              <>
                  <Col md={2}>

                    <div className={styles.createdByWrapper}>
                      <span className={styles.createdBy}>
                        {modifiedDate ? dateFormat(modifiedDate, "dd mmm yyyy") : ""}
                      </span>
                    </div>
                  </Col>
                <Col md={2}>
                    <div className={styles.createdByWrapper}>
                        <span className={styles.createdBy}>
                          <Text
                            className={styles.createdBy}
                            text={createdBy || ""}
                            lines={1}
                          />
                        </span>
                    </div>
                </Col>
                <Col md={5}>
                  <Row className={styles.analyticsWrapper}>
                    <Col md={3} className={styles.views}>
                      {views}
                    </Col>
                    {userRoleService.canShare() && (
                      <Col md={3} className={styles.downloads}>
                        {shares}
                      </Col>
                    )}
                    <Col md={3} className={styles.shares}>
                      {downloads}
                    </Col>
                    <Col md={3} className={styles.timeSpent}>
                      {timeSpent}
                    </Col>
                  </Row>
                </Col>
                <div className={styles.rowBorder}></div>
              </>
            )}
          </Row>
          </NavigateEntity>
        </animated.div>
      ) : null,
    ); 
  }

  const updateSortOrder = (sortBy: string, orderBy: string) => {
    setSortPreference({ sortBy, orderBy });
  }

  const handleLoadMoreAssets = () => {
    if (nextPage) {
      if (entityType === "stream") {
        dispatch(fetchStreamChildren({ id: id || "", page: nextPage, limit: 25, sort: { order: sortPreference.orderBy, type: sortPreference.sortBy } }));
      } else {
        dispatch(fetchGroupChildren({ id: id || "", page: nextPage, limit: 25, sort: { order: sortPreference.orderBy, type: sortPreference.sortBy } }));
      }
    }
  }

  return (
    <div>
      {viewer === "list" &&
        transitions((style, item) =>
          item ? (
            <animated.div style={style}>
              <StreamHeader
                id={id || ""}
                entityType={entityType}
                updateSortOrder={updateSortOrder}
                sortPreference={sortPreference}
              />
            </animated.div>
          ) : null,
        )}

      <InfiniteScroll
        className={styles[`${viewer}`]}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreAssets}
        loading={loading}
        loader={() => {
          return <PlaceholderAssetCard viewer={viewer} />;
        }}
      >
        {allEntities.map((entity) => (
          <React.Fragment key={entity.id}>
            {renderEntityDetails(entity)}
          </React.Fragment>
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default StreamDetailViewers;


export const useStreams = (streamId: string) => {
  return useSelector((state) => {
    const streamsState = _.get(state, `stream.items[${streamId}]`);

    const stream = _.get(streamsState, 'items') || [];
    const loading = _.get(streamsState, 'loadingChildren');
    const total = _.get(streamsState, 'pagination.totalPages', 0);
    const hasMoreItems = _.get(streamsState, 'pagination.hasMoreItems');

    let nextPage: number | null = _.get(streamsState, 'pagination.currentPage', 0) + 1;

    if (nextPage >= total) {
      nextPage = null;
    }

    return {
      stream,
      loading,
      hasMoreItems,
      nextPage
    };
  }, _.isEqual);
}

const PlaceholderAssetCard: React.FC<{ viewer: string }> = ({ viewer }) => {
  return (
    <div className={cx(styles[`placeholder${viewer}`])}>
      <div className={cx(styles.thumbnail, styles.block)}></div>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)}></div>
        <p className={cx(styles.count, styles.block)}></p>
      </div>
    </div>
  );
}

export { PlaceholderAssetCard };